import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { formatCents } from '../lib/api';
import { useBundle, BundleProgress, SavingsRows } from './Bundle';
import { useToast } from '../context/ToastContext';
import { ProductImage } from './ProductImage';

export default function CartDrawer() {
  const cart = useCart();
  const { result, tiers } = useBundle();
  const { info } = useToast();

  return (
    <>
      <div
        className={`fixed inset-0 z-50 bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${
          cart.open ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={() => cart.setOpen(false)}
        aria-hidden
      />
      <aside
        role="dialog"
        aria-label="Cart"
        className={`fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col bg-linen/95 backdrop-blur-xl border-l border-clay/60 shadow-xl transition-transform duration-300 ease-smooth ${
          cart.open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <header className="flex items-center justify-between border-b border-clay/60 px-6 py-5">
          <div>
            <p className="eyebrow">Your cart</p>
            <p className="font-display text-xl font-extrabold text-ink mt-0.5">
              {cart.count} {cart.count === 1 ? 'item' : 'items'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => cart.setOpen(false)}
            className="grid h-9 w-9 place-items-center rounded-xl border border-clay text-ink hover:bg-sand transition-colors"
            aria-label="Close cart"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden>
              <path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {cart.lines.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <div className="grid h-16 w-16 place-items-center rounded-2xl border border-clay/60 bg-sand/50 mb-4">
                <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden className="text-stone/50">
                  <path d="M6 8h12l-1 12H7L6 8z M9 8V6a3 3 0 0 1 6 0v2" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
                </svg>
              </div>
              <p className="font-display text-xl font-extrabold text-ink">Your cart is empty</p>
              <p className="mt-2 max-w-xs text-[14px] text-slate leading-relaxed">
                Add 2+ pieces to unlock bundle savings. Free delivery across London.
              </p>
              <Link to="/shop" onClick={() => cart.setOpen(false)} className="btn-ink mt-6">
                Start shopping
              </Link>
            </div>
          ) : (
            <>
              <BundleProgress result={result} tiers={tiers} />
              <ul className="mt-5 divide-y divide-clay/50">
                {cart.lines.map((l, i) => {
                  const line = result.lines[i];
                  const discounted = line && line.discount_cents > 0;
                  return (
                    <li key={l.product_id} className="flex gap-4 py-4">
                      <Link
                        to={`/product/${l.slug}`}
                        onClick={() => cart.setOpen(false)}
                        className={`block aspect-[4/5] w-20 shrink-0 overflow-hidden rounded-xl ${
                          l.is_transparent ? 'bg-transparent' : 'bg-sand/50'
                        }`}
                      >
                        {l.image && (
                          <ProductImage
                            url={l.image}
                            alt={l.name}
                            isTransparent={l.is_transparent || false}
                            variant="cart"
                            loading="lazy"
                          />
                        )}
                      </Link>
                      <div className="flex flex-1 flex-col">
                        <Link
                          to={`/product/${l.slug}`}
                          onClick={() => cart.setOpen(false)}
                          className="text-[14px] font-semibold text-ink link-underline"
                        >
                          {l.name}
                        </Link>
                        <span className="mt-0.5 font-mono text-[11px] text-stone/60">{l.sku}</span>
                        <div className="mt-auto flex items-end justify-between pt-2">
                          <span className="flex items-baseline gap-1.5 text-[14px]">
                            {discounted ? (
                              <>
                                <span className="font-bold text-plug tabular">{formatCents(line.final_cents)}</span>
                                <span className="text-[12px] text-stone/50 line-through tabular">{formatCents(l.price_cents)}</span>
                              </>
                            ) : (
                              <span className="font-bold text-ink tabular">{formatCents(l.price_cents)}</span>
                            )}
                          </span>
                          <button
                            type="button"
                            onClick={() => { cart.remove(l.product_id); info(`Removed "${l.name}" from cart`); }}
                            className="text-[12px] text-stone link-underline hover:text-wine"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>

        {cart.lines.length > 0 && (
          <footer className="border-t border-clay/60 px-6 py-5 bg-gradient-to-t from-bone to-transparent">
            <SavingsRows result={result} />
            <Link to="/cart" onClick={() => cart.setOpen(false)} className="btn-ink btn-lg mt-4 w-full">
              View cart & checkout
            </Link>
            <button type="button" onClick={() => cart.setOpen(false)} className="btn-bone mt-2.5 w-full">
              Keep shopping
            </button>
          </footer>
        )}
      </aside>
    </>
  );
}
